import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customerAPI, getApiErrorMessage } from '@matix/api-client';
import { safePrecision } from '@matix/utils';
import { useUI } from '../../store/ui';
import { useCart } from '../../store/cart';
import { moneyPrecision } from '../../store/currency';
import { useTenantConfig } from '../../store/tenantConfig';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { useCustomerPicker, tierLabel } from '../../store/customers';
import { useCustomerSearch, useCustomerDetail, useCustomerBranchLinks } from '../customers/hooks';
import { CustomerForm } from '../customers/CustomerForm';
import { CustomerMeasurementsModal } from '../customers/CustomerMeasurementsModal';
import { TIER_THRESHOLDS, avatarColor, getInitials } from '../../lib/mock-data';
import { addressSummary } from '../customers/addressSummary';
import type { Customer, CustomerAddress } from '@matix/types';

/**
 * CustomerPickerModal — backed by live pos-api data.
 *
 * Two views:
 *   - 'picker': split layout. Left = Cash/Credit toggle + search input
 *               (phone keypad OR name search) + results list. Right =
 *               detail panel for the currently-selected customer.
 *   - 'add':    placeholder until POST /customers lands. Currently shows
 *               an info note explaining the limitation.
 *
 * Data flow:
 *   - The list comes from useCustomerSearch(q, sourceType, limit). Empty q
 *     returns the most recent N customers — the cashier doesn't have to
 *     type to find someone they know.
 *   - The detail panel uses useCustomerDetail(id, type) which loads stats
 *     (visits / total / favorite item) lazily on selection.
 *
 * Open behavior is handled by the cart pill / catalog topbar button:
 *   if (cart.customer) → confirm clear, then setCustomer(null)
 *   else               → showModal('customerPicker')
 *
 * On attach, we set cart.customer + close the modal + show a toast.
 */

// ── Search query debounce ────────────────────────────────────────────────
// We debounce the search input so the cashier doesn't fire a request per
// keystroke. 250ms is fast enough to feel instant on a keyboard, slow
// enough to coalesce a phone-pad rapid-tap sequence.
const SEARCH_DEBOUNCE_MS = 250;

function useDebouncedQuery(value: string, delayMs = SEARCH_DEBOUNCE_MS): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// ── Helpers ──────────────────────────────────────────────────────────────

// Defaults to the TENANT currency's precision (3 on KWD) so loyalty balances
// and credit limits aren't shown a fils short of what they are.
function formatMoney(n: number, fractionDigits = safePrecision(moneyPrecision())): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

type Tr = <T,>(opts: { en: T; ar: T }) => T;

function formatLastVisit(iso: string | null, tr: Tr): string {
  if (!iso) return tr({ en: 'Never', ar: 'أبدًا' });
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffHr = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDay = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffHr < 1) return tr({ en: 'Just now', ar: 'الآن' });
  if (diffHr < 24) return tr({ en: `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`, ar: `منذ ${diffHr} ساعة` });
  if (diffDay === 1) return tr({ en: 'Yesterday', ar: 'أمس' });
  if (diffDay < 7) return tr({ en: `${diffDay} days ago`, ar: `منذ ${diffDay} أيام` });
  if (diffDay < 30) return tr({ en: `${Math.floor(diffDay / 7)} weeks ago`, ar: `منذ ${Math.floor(diffDay / 7)} أسابيع` });
  return date.toLocaleDateString();
}

// ── Main component ───────────────────────────────────────────────────────

export function CustomerPickerModal() {
  const openModal = useUI((s) => s.openModal);
  const isOpen = openModal === 'customerPicker';
  const resetPicker = useCustomerPicker((s) => s.resetPicker);

  useEffect(() => {
    if (isOpen) resetPicker();
  }, [isOpen, resetPicker]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay show modal-overlay-sheet"
      onClick={(e) => e.target === e.currentTarget && useUI.getState().closeModal()}
    >
      <div className="modal customer-modal">
        <CustomerPickerInner />
      </div>
    </div>
  );
}

function CustomerPickerInner() {
  const view = useCustomerPicker((s) => s.view);
  return view === 'add' ? <AddView /> : <PickerView />;
}

// ── Picker view ──────────────────────────────────────────────────────────

function PickerView() {
  const closeModal = useUI((s) => s.closeModal);
  const selectedId = useCustomerPicker((s) => s.selectedId);
  const { t } = useLang();

  return (
    <>
      <div className="modal-header" id="custModalHeader">
        <div className="modal-header-img" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>
          <UserPlusIcon size={28} />
        </div>
        <div className="modal-header-title">
          <div className="modal-header-name">{t({ en: 'Find or add customer', ar: 'البحث عن عميل أو إضافته' })}</div>
          <div className="modal-header-name-ar">{t({ en: 'Search by name, or switch to phone', ar: 'ابحث بالاسم، أو بدّل إلى الهاتف' })}</div>
        </div>
        <SourceTypeToggle />
        <button className="modal-close" onClick={closeModal} aria-label={t({ en: 'Close', ar: 'إغلاق' })} style={{ marginLeft: 8 }}>
          <XIcon size={18} />
        </button>
      </div>

      {/*
        `has-selection` drives the narrow-screen master/detail switch: below
        the two-column breakpoint only one side is on screen at a time — the
        list until a customer is picked, the detail (with its Attach button)
        after. On wide screens the class is inert and both sides show.
      */}
      <div className={`cust-picker-grid ${selectedId != null ? 'has-selection' : ''}`}>
        <ListSide />
        <DetailSide />
      </div>
    </>
  );
}

/**
 * Cash vs Credit source toggle. Lives in the modal header so it's always
 * visible regardless of which view is active.
 */
function SourceTypeToggle() {
  const sourceType = useCustomerPicker((s) => s.sourceType);
  const setSourceType = useCustomerPicker((s) => s.setSourceType);
  const { t: tr } = useLang();

  return (
    <div
      className="cust-source-toggle"
      style={{
        display: 'inline-flex',
        background: 'var(--surface-2)',
        borderRadius: 8,
        padding: 3,
        gap: 2,
        marginLeft: 'auto',
        marginRight: 8,
      }}
    >
      {(['cash', 'credit'] as const).map((t) => (
        <button
          key={t}
          onClick={() => setSourceType(t)}
          style={{
            border: 0,
            padding: '6px 14px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            background: sourceType === t ? 'var(--surface)' : 'transparent',
            color: sourceType === t ? 'var(--text)' : 'var(--text-muted)',
            boxShadow: sourceType === t ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            transition: 'all 0.12s',
          }}
        >
          {t === 'cash' ? tr({ en: 'Cash customers', ar: 'عملاء نقديون' }) : tr({ en: 'Credit (GL)', ar: 'آجل (أستاذ عام)' })}
        </button>
      ))}
    </div>
  );
}

function ListSide() {
  const sourceType = useCustomerPicker((s) => s.sourceType);
  const searchMode = useCustomerPicker((s) => s.searchMode);
  const searchText = useCustomerPicker((s) => s.searchText);
  const setSearchMode = useCustomerPicker((s) => s.setSearchMode);
  const setSearchText = useCustomerPicker((s) => s.setSearchText);
  const appendDigit = useCustomerPicker((s) => s.appendDigit);
  const backspace = useCustomerPicker((s) => s.backspace);
  const clearSearch = useCustomerPicker((s) => s.clearSearch);
  const setView = useCustomerPicker((s) => s.setView);
  const setEditingId = useCustomerPicker((s) => s.setEditingId);
  const { t: tr } = useLang();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [searchMode, sourceType]);

  const showClear = searchText.length > 0;

  return (
    <div className="cust-list-side">
      <div className="cust-search">
        <div className="cust-search-row">
          <div className="cust-search-wrap">
            <SearchIcon size={18} />
            <input
              ref={inputRef}
              type="text"
              placeholder={searchMode === 'phone' ? tr({ en: 'Phone number...', ar: 'رقم الهاتف...' }) : tr({ en: 'Customer name...', ar: 'اسم العميل...' })}
              inputMode={searchMode === 'phone' ? 'tel' : 'text'}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              autoComplete="off"
            />
            <button className={`clear ${showClear ? 'show' : ''}`} onClick={clearSearch} aria-label={tr({ en: 'Clear search', ar: 'مسح البحث' })}>
              <XIcon size={13} strokeWidth={2.5} />
            </button>
          </div>
          <div className="cust-search-mode">
            <button
              className={searchMode === 'phone' ? 'active' : ''}
              onClick={() => setSearchMode('phone')}
            >
              <PhoneIcon size={13} />
              {tr({ en: 'Phone', ar: 'الهاتف' })}
            </button>
            <button
              className={searchMode === 'name' ? 'active' : ''}
              onClick={() => setSearchMode('name')}
            >
              <UserIcon size={13} />
              {tr({ en: 'Name', ar: 'الاسم' })}
            </button>
          </div>
        </div>

        {searchMode === 'phone' && (
          <div className="cust-quick-pad" style={{ display: 'grid' }}>
            {(['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const).map((d) => (
              <button key={d} onClick={() => appendDigit(d)}>{d}</button>
            ))}
            <button className="action" onClick={() => appendDigit('+')}>+</button>
            <button onClick={() => appendDigit('0')}>0</button>
            <button className="action danger" onClick={backspace}>⌫</button>
          </div>
        )}
      </div>

      <ResultsList />

      {/* Add-new is cash-only — credit (GL) customers are created in the ERP
          back-office, so the button isn't shown on the Credit tab. */}
      {sourceType !== 'credit' && (
        <div className="cust-list-footer">
          <button
            className="cust-add-btn"
            onClick={() => {
              setEditingId(null);
              setView('add');
            }}
          >
            <UserPlusIcon size={14} strokeWidth={2.5} />
            {tr({ en: 'Add new customer', ar: 'إضافة عميل جديد' })}
          </button>
        </div>
      )}
    </div>
  );
}

function ResultsList() {
  const sourceType = useCustomerPicker((s) => s.sourceType);
  const searchText = useCustomerPicker((s) => s.searchText);
  const selectedId = useCustomerPicker((s) => s.selectedId);
  const selectId = useCustomerPicker((s) => s.selectId);
  const { t: tr } = useLang();

  // Debounce: don't fire a request on every keystroke.
  const debouncedQ = useDebouncedQuery(searchText.trim());

  const { data, isLoading, error } = useCustomerSearch(debouncedQ, sourceType, 20);
  const customers = data?.customers ?? [];

  // Loading state on initial fetch.
  if (isLoading && customers.length === 0) {
    return (
      <div className="cust-results">
        <div className="cust-empty-prompt">
          <div className="cust-empty-prompt-icon">
            <SearchIcon size={28} strokeWidth={1.6} stroke="var(--text-dim)" />
          </div>
          <div className="cust-empty-prompt-text">{tr({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>
        </div>
      </div>
    );
  }

  // Error state — be informative but not alarming.
  if (error) {
    return (
      <div className="cust-results">
        <div className="cust-empty-prompt">
          <div className="cust-empty-prompt-icon">
            <AlertCircleIcon size={28} stroke="var(--danger)" />
          </div>
          <div className="cust-empty-prompt-text">{tr({ en: "Couldn't load customers", ar: 'تعذّر تحميل العملاء' })}</div>
          <div className="cust-empty-prompt-hint">
            {sourceType === 'credit'
              ? tr({ en: 'Credit customers come from the ERP — check the connection.', ar: 'عملاء الآجل يأتون من نظام ERP — تحقق من الاتصال.' })
              : tr({ en: 'Try again in a moment.', ar: 'حاول مرة أخرى بعد لحظات.' })}
          </div>
        </div>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="cust-results">
        <div className="cust-empty-prompt">
          <div className="cust-empty-prompt-icon">
            <SearchIcon size={28} strokeWidth={1.6} stroke="var(--text-dim)" />
          </div>
          <div className="cust-empty-prompt-text">
            {searchText
              ? tr({ en: `No matches for "${searchText}"`, ar: `لا توجد نتائج لـ "${searchText}"` })
              : sourceType === 'cash'
                ? tr({ en: 'No cash customers found', ar: 'لا يوجد عملاء نقديون' })
                : tr({ en: 'No credit customers found', ar: 'لا يوجد عملاء آجل' })}
          </div>
          <div className="cust-empty-prompt-hint">
            {searchText ? tr({ en: 'Tap "Add new customer" to create one', ar: 'اضغط "إضافة عميل جديد" لإنشاء عميل' }) : tr({ en: 'Type a phone or name to search', ar: 'اكتب هاتفًا أو اسمًا للبحث' })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cust-results">
      {customers.map((c) => (
        <CustomerRow
          key={c.id}
          customer={c}
          selected={selectedId === c.id}
          onClick={() => selectId(c.id)}
        />
      ))}
    </div>
  );
}

function CustomerRow({
  customer: c,
  selected,
  onClick,
}: {
  customer: Customer;
  selected: boolean;
  onClick: () => void;
}) {
  const { t: tr } = useLang();
  const display = c.name ?? `#${c.code ?? c.id}`;
  return (
    <div className={`cust-row ${selected ? 'selected' : ''}`} onClick={onClick}>
      <div className="cust-row-avatar" style={{ background: avatarColor(c.id) }}>
        {getInitials(display)}
      </div>
      <div className="cust-row-body">
        <div className="cust-row-name">
          <span>{display}</span>
          <span className={`cust-row-tier-badge tier-${c.tier}`}>{tierLabel(c.tier)}</span>
        </div>
        <div className="cust-row-phone">{c.phone || '—'}</div>
      </div>
      <div className="cust-row-meta">
        {c.loyalty_balance != null && (
          <div className="cust-row-points">{c.loyalty_balance.toLocaleString()} {tr({ en: 'pts', ar: 'نقطة' })}</div>
        )}
        {c.code && c.type === 'credit' && (
          <div className="cust-row-visits">{tr({ en: `Code ${c.code}`, ar: `رمز ${c.code}` })}</div>
        )}
      </div>
    </div>
  );
}

/**
 * The customer's addresses, and — when there is more than one — which of them
 * this order is for.
 *
 * Two things were wrong before this existed. The row rendered `c.address`,
 * a scalar the service pins to null for every CASH customer because
 * Inv_CashCustomer has no such column: the real addresses live in
 * `addresses[]` (Inv_CashCustomerDetail). So a cash customer with two
 * addresses on file read "No address on file", on every tenant. And even once
 * they are visible, nobody could say which one the order was for — the receipt
 * fell back to the customer's most recent, which is a guess the person at the
 * counter can make and the database cannot.
 *
 * Choosing is OPTIONAL and only offered on the second address. One address is
 * shown as plain text and sent as the pick, because there is nothing to
 * choose between; none prints the empty line it always did. A CREDIT customer
 * keeps the scalar `address` off their GL account — they have no
 * Inv_CashCustomerDetail rows at all.
 */
function AddressRows({
  customer: c,
  selectedId,
  onSelect,
}: {
  customer: Customer;
  selectedId: number | null;
  onSelect: (id: number | null) => void;
}): JSX.Element {
  const { t: tr } = useLang();
  const addresses: CustomerAddress[] = c.type === 'cash' ? c.addresses ?? [] : [];

  if (c.type === 'credit') {
    return (
      <div className={`cust-contact-row ${c.address ? '' : 'empty'}`}>
        <div className="icon"><MapPinIcon size={14} /></div>
        <span className="v">
          {c.address || tr({ en: 'No address on file', ar: 'لا يوجد عنوان مسجّل' })}
        </span>
      </div>
    );
  }

  if (addresses.length === 0) {
    return (
      <div className="cust-contact-row empty">
        <div className="icon"><MapPinIcon size={14} /></div>
        <span className="v">{tr({ en: 'No address on file', ar: 'لا يوجد عنوان مسجّل' })}</span>
      </div>
    );
  }

  if (addresses.length === 1) {
    return (
      <div className="cust-contact-row addr">
        <div className="icon"><MapPinIcon size={14} /></div>
        <span className="v">{addressSummary(addresses[0]!, tr)}</span>
      </div>
    );
  }

  return (
    <div className="cust-address-choice">
      <div className="cust-address-choice-head">
        <MapPinIcon size={14} />
        <span>
          {tr({
            en: `Which address is this order for? (${addresses.length} on file)`,
            ar: `أي عنوان يخص هذا الطلب؟ (${addresses.length} عناوين مسجّلة)`,
          })}
        </span>
      </div>
      <ul className="cust-address-list" role="radiogroup">
        {addresses.map((a) => (
          <li key={a.id} className={a.id === selectedId ? 'selected' : ''}>
            <label>
              <input
                type="radio"
                name="cust-order-address"
                checked={a.id === selectedId}
                onChange={() => onSelect(a.id)}
              />
              <span className="v">{addressSummary(a, tr)}</span>
            </label>
          </li>
        ))}
      </ul>
      {selectedId == null && (
        <div className="cust-address-hint">
          {tr({
            en: 'Nothing picked — the order records their most recent address.',
            ar: 'لم يتم الاختيار — سيُسجَّل الطلب على أحدث عنوان لديهم.',
          })}
        </div>
      )}
    </div>
  );
}

function DetailSide() {
  const sourceType = useCustomerPicker((s) => s.sourceType);
  const selectedId = useCustomerPicker((s) => s.selectedId);
  const { data: customer, isLoading } = useCustomerDetail(selectedId, sourceType);
  const { t: tr } = useLang();

  if (selectedId == null) {
    return (
      <div className="cust-detail-side">
        <div className="cust-detail-empty">
          <div className="cust-detail-empty-icon">
            <UserCircleIcon size={40} strokeWidth={1.4} stroke="var(--text-dim)" />
          </div>
          <div className="cust-detail-empty-title">{tr({ en: 'No customer selected', ar: 'لم يتم اختيار عميل' })}</div>
          <div className="cust-detail-empty-text">
            {tr({ en: 'Search a phone number on the left or pick a result to see their loyalty status and history.', ar: 'ابحث برقم هاتف على اليسار أو اختر نتيجة لعرض حالة الولاء والسجل.' })}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !customer) {
    return (
      <div className="cust-detail-side">
        <DetailBackBar />
        <div className="cust-detail-empty">
          <div className="cust-detail-empty-title">{tr({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cust-detail-side">
      <DetailBackBar />
      <DetailContent customer={customer} />
    </div>
  );
}

/**
 * Narrow-screen only (CSS hides it once the two-column grid kicks in): on a
 * phone the detail panel covers the list, so deselecting is the only way back
 * to the search results.
 */
function DetailBackBar() {
  const selectId = useCustomerPicker((s) => s.selectId);
  const { t: tr } = useLang();
  return (
    <button className="cust-detail-back" onClick={() => selectId(null)}>
      <ArrowLeftIcon size={15} />
      {tr({ en: 'Back to results', ar: 'العودة إلى النتائج' })}
    </button>
  );
}

function DetailContent({ customer: c }: { customer: Customer }) {
  const setView = useCustomerPicker((s) => s.setView);
  const setEditingId = useCustomerPicker((s) => s.setEditingId);
  const setCartCustomer = useCart((s) => s.setCustomer);
  const cartCustomer = useCart((s) => s.customer);
  const cartAddressId = useCart((s) => s.customerAddressId);
  const setCartAddressId = useCart((s) => s.setCustomerAddressId);
  const closeModal = useUI((s) => s.closeModal);
  // Feature gate (mstr_ConfigurationParameter → CustomerBranchLink): the
  // branch-visibility section only appears when the tenant enabled it.
  const branchLinkEnabled = useTenantConfig((s) => s.customerBranchLink);
  // POS_CustomerMeasurements: show / edit this customer's measurements (cash or credit).
  const measurementsOn = useTenantConfig((s) => s.customerMeasurements);
  const [measuresOpen, setMeasuresOpen] = useState(false);
  const { t: tr } = useLang();

  const tierMeta = TIER_THRESHOLDS[c.tier];
  const display = c.name ?? `#${c.code ?? c.id}`;
  const loyalty = c.loyalty_balance ?? 0;
  const stats = c.stats;

  // Same identity check both panels need: this customer is already on
  // the order if the cart's attached customer matches by id+type. We
  // check `type` too so a cash customer with id=5 doesn't collide with
  // a credit customer that happens to share that id.
  const isCurrentlyAttached =
    cartCustomer != null && cartCustomer.id === c.id && cartCustomer.type === c.type;

  // Which address the cashier has picked, held HERE rather than in the cart
  // until they attach: browsing other customers must not rewrite the order,
  // and `setCustomer` deliberately clears the cart's address, so a pick
  // written straight to the cart would be wiped by the attach that follows it.
  //
  // Seeded from the cart when this IS the attached customer, so reopening the
  // panel to change the address shows what the order currently says. One
  // address needs no choosing and is adopted outright.
  const soleAddressId =
    c.type === 'cash' && (c.addresses?.length ?? 0) === 1 ? c.addresses![0]!.id : null;
  const [addressId, setAddressId] = useState<number | null>(
    isCurrentlyAttached ? cartAddressId ?? soleAddressId : soleAddressId,
  );
  // The detail arrives after the first render (and again after an edit), so
  // the sole-address case has to be picked up when it lands, not only on mount.
  useEffect(() => {
    if (soleAddressId != null) setAddressId(soleAddressId);
  }, [soleAddressId]);

  const onAttach = () => {
    // Order matters: setCustomer clears the address by design (it belongs to
    // whoever was attached), so the pick is committed AFTER it.
    setCartCustomer(c);
    setCartAddressId(addressId);
    closeModal();
    toast(
      tr({
        en: `${display} attached${
          c.loyalty_balance != null ? ` · ${c.loyalty_balance.toLocaleString()} pts available` : ''
        }`,
        ar: `تم ربط ${display}${
          c.loyalty_balance != null ? ` · ${c.loyalty_balance.toLocaleString()} نقطة متاحة` : ''
        }`,
      }),
      'success',
    );
  };

  const onRemove = () => {
    setCartCustomer(null);
    closeModal();
    toast(tr({ en: `${display} removed from order`, ar: `تمت إزالة ${display} من الطلب` }), 'success');
  };

  const onEdit = () => {
    setEditingId(c.id);
    setView('add');
  };

  return (
    <>
      <div className="cust-detail-content">
        <div className="cust-profile-header">
          <div className="cust-profile-avatar" style={{ background: avatarColor(c.id) }}>
            {getInitials(display)}
          </div>
          <div className="cust-profile-info">
            <div className="cust-profile-name">{display}</div>
            <div className="cust-profile-phone">{c.phone || '—'}</div>
            {c.name_ar && (
              <div className="cust-profile-phone ar" style={{ marginTop: 2 }}>{c.name_ar}</div>
            )}
          </div>
        </div>

        {/*
          Tier card. For credit customers we don't have a loyalty balance,
          so we collapse the card to just a "Credit customer · GL code"
          line — no points, no progress bar.
        */}
        {c.type === 'cash' ? (
          <div className={`cust-tier-card ${c.tier}`}>
            <div className="cust-tier-card-info">
              <div className="cust-tier-tier">{tierLabel(c.tier)} {tr({ en: 'tier', ar: 'الفئة' })}</div>
              <div className="cust-tier-points">{loyalty.toLocaleString()}</div>
              <div className="cust-tier-points-label">{tr({ en: 'loyalty points', ar: 'نقاط الولاء' })}</div>
              {tierMeta.next && tierMeta.nextAt != null ? (
                <>
                  <div className="cust-tier-progress">
                    <div
                      className="cust-tier-progress-fill"
                      style={{
                        width: `${Math.min(
                          100,
                          ((loyalty - tierMeta.min) / (tierMeta.nextAt - tierMeta.min)) * 100,
                        )}%`,
                      }}
                    />
                  </div>
                  <div className="cust-tier-next">
                    {tr({ en: `${(tierMeta.nextAt - loyalty).toLocaleString()} pts to`, ar: `${(tierMeta.nextAt - loyalty).toLocaleString()} نقطة للوصول إلى` })} {tierLabel(tierMeta.next)}
                  </div>
                </>
              ) : (
                <div className="cust-tier-next">
                  {tr({ en: `Highest tier · ${loyalty.toLocaleString()} lifetime points`, ar: `أعلى فئة · ${loyalty.toLocaleString()} نقطة تراكمية` })}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '14px 16px',
              borderRadius: 12,
              background: 'var(--surface-2)',
              marginBottom: 16,
              fontSize: 13,
              color: 'var(--text-muted)',
            }}
          >
            {tr({ en: 'Credit customer · GL code', ar: 'عميل آجل · رمز الأستاذ العام' })} <strong>{c.code ?? '—'}</strong>
          </div>
        )}

        {/* Stats grid — only for cash customers (credit has no POS history yet) */}
        {c.type === 'cash' && stats && (
          <div className="cust-stats-grid">
            <div className="cust-stat">
              <div className="cust-stat-label">{tr({ en: 'Last visit', ar: 'آخر زيارة' })}</div>
              <div className="cust-stat-value" style={{ fontSize: 14 }}>
                {formatLastVisit(stats.last_visit, tr)}
              </div>
            </div>
            <div className="cust-stat">
              <div className="cust-stat-label">{tr({ en: 'Lifetime visits', ar: 'إجمالي الزيارات' })}</div>
              <div className="cust-stat-value">{stats.visits ?? 0}</div>
            </div>
            <div className="cust-stat">
              <div className="cust-stat-label">{tr({ en: 'Total spent', ar: 'إجمالي الإنفاق' })}</div>
              <div className="cust-stat-value" style={{ fontSize: 16 }}>
                {formatMoney(stats.total_spent ?? 0)}
              </div>
            </div>
            <div className="cust-stat">
              <div className="cust-stat-label">{tr({ en: 'Average spend', ar: 'متوسط الإنفاق' })}</div>
              <div className="cust-stat-value">
                {stats.avg_spend != null ? formatMoney(stats.avg_spend) : '—'}
              </div>
              <div className="cust-stat-meta">{tr({ en: 'per visit', ar: 'لكل زيارة' })}</div>
            </div>
          </div>
        )}

        {c.type === 'cash' && stats?.favorite_item && (
          <div className="cust-history-section">
            <div className="cust-section-title">{tr({ en: 'Favorite item', ar: 'الصنف المفضل' })}</div>
            <div className="cust-fav-item">
              <div className="cust-fav-emoji">⭐</div>
              <div>
                <div className="cust-fav-name">
                  {stats.favorite_item.name ?? `#${stats.favorite_item.item_id}`}
                </div>
                <div className="cust-fav-meta">
                  {tr({
                    en: `Ordered ${stats.favorite_item.times_ordered} time${stats.favorite_item.times_ordered === 1 ? '' : 's'}`,
                    ar: `طُلب ${stats.favorite_item.times_ordered} مرة`,
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="cust-history-section">
          <div className="cust-section-title">{tr({ en: 'Contact', ar: 'التواصل' })}</div>
          <div className="cust-contact-list">
            <div className={`cust-contact-row ${c.email ? '' : 'empty'}`}>
              <div className="icon"><MailIcon size={14} /></div>
              <span className="v">{c.email || tr({ en: 'No email on file', ar: 'لا يوجد بريد إلكتروني مسجّل' })}</span>
            </div>
            <AddressRows customer={c} selectedId={addressId} onSelect={setAddressId} />
          </div>
        </div>

        {branchLinkEnabled && <BranchLinksSection customerId={c.id} type={c.type} />}
      </div>

      <div className="cust-detail-actions">
        {measurementsOn && (
          <button className="btn-secondary" onClick={() => setMeasuresOpen(true)}>
            {tr({ en: 'Measurements', ar: 'المقاسات' })}
          </button>
        )}
        {c.type === 'cash' && (
          <button className="btn-secondary" onClick={onEdit}>
            <EditIcon size={14} />
            {tr({ en: 'Edit', ar: 'تعديل' })}
          </button>
        )}
        {isCurrentlyAttached && (
          <button
            className="btn-secondary"
            onClick={onRemove}
            style={{ color: 'var(--danger)' }}
            title={tr({ en: 'Remove customer from this order', ar: 'إزالة العميل من هذا الطلب' })}
          >
            <XIcon size={14} />
            {tr({ en: 'Remove', ar: 'إزالة' })}
          </button>
        )}
        <button className="btn-primary-modal" onClick={onAttach}>
          {isCurrentlyAttached ? tr({ en: 'Keep attached', ar: 'إبقاء مرتبطًا' }) : tr({ en: 'Attach to order', ar: 'ربط بالطلب' })}
          <CheckIcon size={14} style={{ verticalAlign: 'middle', marginLeft: 6 }} />
        </button>
      </div>
      {measuresOpen && (
        <CustomerMeasurementsModal
          customerType={c.type}
          customerId={c.id}
          customerName={display}
          onClose={() => setMeasuresOpen(false)}
        />
      )}
    </>
  );
}

// ── Branch visibility section ────────────────────────────────────────────
//
// Manages the customer ↔ branch links (pos_customer_branches, migration 029)
// — the same links the admin "Customer branches" screen owns, so a customer
// can be linked to branches from the cashier popup too. Empty selection means
// the customer is visible in every branch (the legacy fallback).

function BranchLinksSection({
  customerId,
  type,
}: {
  customerId: number;
  type: 'cash' | 'credit';
}) {
  const { t: tr } = useLang();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useCustomerBranchLinks(customerId, type);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [dirty, setDirty] = useState(false);

  // Sync local selection whenever the loaded links change (new customer or a
  // refetch after saving). Until then the section shows a loading state.
  useEffect(() => {
    if (data) {
      setSelected(new Set(data.branch_ids));
      setDirty(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (branchIds: number[]) => customerAPI.saveBranchLinks(customerId, type, branchIds),
    onSuccess: (res) => {
      queryClient.setQueryData(['customers', 'branches', type, customerId], res);
      setSelected(new Set(res.branch_ids));
      setDirty(false);
      // Visibility may have changed → let the search lists refetch.
      void queryClient.invalidateQueries({ queryKey: ['customers', 'search'] });
      toast(tr({ en: 'Branch visibility saved', ar: 'تم حفظ ظهور الفروع' }), 'success');
    },
    onError: (err) => {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      if (ax.response?.status === 503) {
        toast(tr({ en: 'Branch links are not enabled for this tenant yet', ar: 'ربط الفروع غير مُفعّل لهذا الحساب بعد' }), 'error');
      } else {
        toast(ax.response?.data?.error ?? getApiErrorMessage(err), 'error');
      }
    },
  });

  const toggle = (branchId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(branchId)) next.delete(branchId);
      else next.add(branchId);
      return next;
    });
    setDirty(true);
  };

  const branches = data?.branches ?? [];

  return (
    <div className="cust-history-section">
      <div className="cust-section-title">{tr({ en: 'Branch visibility', ar: 'ظهور الفروع' })}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        {selected.size === 0
          ? tr({ en: 'Visible in all branches. Pick branches to restrict.', ar: 'يظهر في كل الفروع. اختر فروعًا لتقييد الظهور.' })
          : tr({ en: `Visible only in ${selected.size} branch(es).`, ar: `يظهر فقط في ${selected.size} فرع/فروع.` })}
      </div>

      {isLoading ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{tr({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>
      ) : error ? (
        <div style={{ fontSize: 13, color: 'var(--danger)' }}>{tr({ en: "Couldn't load branches", ar: 'تعذّر تحميل الفروع' })}</div>
      ) : branches.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{tr({ en: 'No branches found', ar: 'لا توجد فروع' })}</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {branches.map((b) => {
              const on = selected.has(b.id);
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => toggle(b.id)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                    border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                    background: on ? 'var(--primary-light)' : 'var(--surface)',
                    color: on ? 'var(--primary)' : 'var(--text)',
                    fontWeight: on ? 600 : 400,
                  }}
                >
                  {on && <CheckIcon size={13} />}
                  {b.name || b.name_ar || `#${b.id}`}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 10 }}>
            <button
              className="btn-secondary"
              onClick={() => save.mutate([...selected])}
              disabled={!dirty || save.isPending}
              style={{ opacity: dirty && !save.isPending ? 1 : 0.55, cursor: dirty && !save.isPending ? 'pointer' : 'default' }}
            >
              {save.isPending ? tr({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : tr({ en: 'Save branch visibility', ar: 'حفظ ظهور الفروع' })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Add / Edit view ──────────────────────────────────────────────────────
//
// Both add and edit render the full CustomerForm (identity + classification +
// multiple addresses + branch visibility). Credit (GL) customers are managed
// in the ERP back-office, so the credit tab shows a notice instead.

function AddView() {
  const setView = useCustomerPicker((s) => s.setView);
  const setEditingId = useCustomerPicker((s) => s.setEditingId);
  const setSelectedId = useCustomerPicker((s) => s.selectId);
  const editingId = useCustomerPicker((s) => s.editingId);
  const sourceType = useCustomerPicker((s) => s.sourceType);
  const searchText = useCustomerPicker((s) => s.searchText);

  const isEdit = editingId != null;

  // Pre-fill add mode from what the cashier searched for, judged by WHAT it is
  // rather than which tab it was typed on: a number is a phone number whichever
  // tab it came from, anything else is a name. Seeding by tab put a phone typed
  // on the Name tab into the name field.
  const seed = isEdit ? '' : searchText.trim();
  const seedIsPhone = /^\+?[\d\s()-]+$/.test(seed);

  const back = () => {
    setEditingId(null);
    setView('picker');
  };

  // Credit customers (add or edit) live in the ERP back-office.
  if (sourceType === 'credit') {
    return <CreditCustomerNotice onBack={back} />;
  }

  return (
    <CustomerForm
      mode={isEdit ? 'edit' : 'add'}
      customerId={editingId}
      initialPhone={seedIsPhone ? seed : ''}
      initialName={seedIsPhone ? '' : seed}
      onCancel={back}
      onSaved={(id) => {
        setEditingId(null);
        setSelectedId(id);
        setView('picker');
      }}
    />
  );
}

function CreditCustomerNotice({ onBack }: { onBack: () => void }) {
  const { t: tr } = useLang();
  return (
    <>
      <div className="add-form-header">
        <button className="add-form-back" onClick={onBack} title={tr({ en: 'Back', ar: 'رجوع' })} aria-label={tr({ en: 'Back', ar: 'رجوع' })}>
          <ArrowLeftIcon size={16} />
        </button>
        <div>
          <div className="add-form-title">{tr({ en: 'Add credit customer', ar: 'إضافة عميل آجل' })}</div>
          <div className="add-form-sub">{tr({ en: 'Use the ERP back-office', ar: 'استخدم نظام ERP' })}</div>
        </div>
      </div>
      <div
        style={{
          padding: 40,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          textAlign: 'center',
          minHeight: 320,
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'var(--surface-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AlertCircleIcon size={32} stroke="var(--text-dim)" />
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
          {tr({ en: 'Credit customers must be created in the ERP', ar: 'يجب إنشاء عملاء الآجل في نظام ERP' })}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 380, lineHeight: 1.5 }}>
          {tr({
            en: 'A credit (GL) customer needs a chart-of-accounts entry under the subsidiary\'s receivables parent. That setup happens in the ERP back-office. Switch to "Cash customers" above to add a quick customer here.',
            ar: 'يحتاج عميل الآجل (الأستاذ العام) إلى حساب ضمن دليل الحسابات تحت حساب المدينين للشركة الفرعية. يتم هذا الإعداد في نظام ERP. بدّل إلى "عملاء نقديون" أعلاه لإضافة عميل سريع هنا.',
          })}
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn-secondary" onClick={onBack}>{tr({ en: 'Back to search', ar: 'العودة إلى البحث' })}</button>
      </div>
    </>
  );
}

// ── Inline icons ─────────────────────────────────────────────────────────

interface IconProps {
  size?: number;
  strokeWidth?: number;
  stroke?: string;
  style?: React.CSSProperties;
}

function SearchIcon({ size = 18, strokeWidth = 2, stroke = 'currentColor' }: IconProps) {
  return (
    <svg className="icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function PhoneIcon({ size = 14 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function UserIcon({ size = 14 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function UserPlusIcon({ size = 24, strokeWidth = 2 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}

function UserCircleIcon({ size = 40, strokeWidth = 1.4, stroke = 'currentColor' }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 22a8 8 0 0 1 16 0" />
    </svg>
  );
}

function XIcon({ size = 18, strokeWidth = 2 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function MailIcon({ size = 14 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function MapPinIcon({ size = 14 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function AlertCircleIcon({ size = 14, stroke = 'currentColor' }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={2}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function EditIcon({ size = 14 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function CheckIcon({ size = 14, style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ArrowLeftIcon({ size = 16 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}
